// Core packages
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// Third party packages
import { MatDialog } from "@angular/material";
import { TranslateService } from "@ngx-translate/core";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { PropertiesService } from "src/app/modules/properties/properties.service";
import { RolesService } from "src/app/modules/roles/roles.service";
import { SettingsService } from "src/app/modules/settings/settings.service";
import { PropertyInterface } from "src/app/shared/models/property/property.interface";
import { Property } from "src/app/shared/models/property/property.model";
import { IUser } from "src/app/shared/models/user/user.interface";
import { DateTimeFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { ApiService } from "src/app/shared/services/api.service";
import { ConfigService } from "src/app/shared/services/config.service";
import { ThemeService } from "src/app/shared/services/theme.service";
import { CondoStructuresService } from "../../condo-structures.service";

@Component({
  selector: "app-property-mapping",
  templateUrl: "./property-mapping.page.html",
  styleUrls: ["./property-mapping.page.scss"]
})
export class PropertyMappingComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  loggedUserRole: any;
  currentRole: any;
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  // Could not be "Property" type because in "get" method in the back-end
  // We are populating some fields, so the model is not the same!
  public property: Property = new Property();
  propertyData: PropertyInterface;
  loggedUserShared = false;
  loggedUserMaster = false;
  dateTimeFormat: DateTimeFormatPipe;
  mappingFormActiveSection: string; // Active mapping section
  itemId: string; // Id of active mapping section item
  propertyId = "";
  condoStructure: any;
  documentArchive: any;
  pageIsLoaded = false;

  /**
   * Class constructor
   */
  constructor(
    private configService: ConfigService,
    private activatedRoute: ActivatedRoute,
    private propertiesService: PropertiesService,
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private authService: AuthService,
    public dialog: MatDialog,
    private cookieService: CookieService,
    private apiService: ApiService, // DO NOT MOVE since it's used for 'autocomplete' field type
    private condoStructuresService: CondoStructuresService,
    private rolesService: RolesService,
    private settingsService: SettingsService,
    // private companiesService: CompaniesService,
  ) {}

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(
      this.translateService.instant("properties.structures.pageTitle")
    );

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );
    // this.subscriptions.push(this.authService.loggedUserRequestedRole$.subscribe(res => this.loggedUserRequestedRole = res));

    // Get logged user's role data
    this.currentRole = this.loggedUser.userRoles.find((obj: any) => {
      return (
        obj._id.toString() ===
        this.authService.loggedUserRequestedRole$.getValue().toString()
      );
    });
    this.loggedUserRole = this.currentRole.role.type;
    if (this.currentRole.role.type === "shared") {
      this.loggedUserShared = true;
    }
    if (this.currentRole.role.type === "master" || this.currentRole.role.type === "collaborator") {
      this.loggedUserMaster = true;
    }
    // this.subscriptions.push(
    //   this.rolesService
    //     .get(this.authService.loggedUserRequestedRole$.getValue())
    //     .subscribe(res2 => {
    //       if (res2.status) {
    //         this.loggedUserRole = res2.role;
    //         this.loggedUserMaster =
    //           this.loggedUserRole.type === "master" ||
    //           this.loggedUserRole.type === "collaborator";
    //       }
    //     })
    // );

    // Get current lang
    this.lang =
      this.cookieService.get("lang") || this.configService.defaultLang;

    // this.activatedRoute.queryParams.subscribe(params => {
    // });

    // Subscribe to formActiveSection changes
    this.subscriptions.push(
      this.activatedRoute.params.subscribe(param => {
        if (param.mappingFormActiveSection) {
          this.mappingFormActiveSection = param.mappingFormActiveSection;
        }
        if (param.itemId) {
          this.itemId = param.itemId;
        }
      })
    );

    // Get requested property data
    this.propertyId = this.activatedRoute.snapshot.params.propertyId;
    this.subscriptions.push(
      this.propertiesService.get(this.propertyId).subscribe(
        res => {
          if (res.status) {
            // Set permission data
            this.property.setData(res.data);
            this.propertyData = res.data;

            // If property has a structureID, than retrive related data
            if (res.data.structure) {
              this.getStructureData();
            }
            this.pageIsLoaded = true;
            return;
          }

          // Something bad happened
          // redirect user to home
          this.router.navigate([this.lang, "app", "properties", "list"]);
        },
        err => {
          // if we came here users with given userId do not exist
          this.router.navigate([this.lang, "errors", "404"]);
        }
      )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Get courrent proeprty condoStructure data and populate
   * this.condoStructure
   *
   * @since 1.0.0
   */
  getStructureData(): void {
    this.condoStructuresService
      .get(this.property.get("structure"))
      .subscribe(res => {
        if (res.status) {
          this.condoStructure = res.data;
        } else {
          this.toastrService.error(res.message);
        }
      });
  }

  /**
   * Create condoStructure for current property
   *
   * @since 1.0.0
   */
  onCreateStructure(): void {
    this.condoStructuresService.create(this.property.getId()).subscribe(res => {
      if (!res.status) {
        this.toastrService.error(res.message);
        return;
      }
      this.property.set("structure", res.item._id);
      this.condoStructure = res.item;
    });
  }
}
